import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Button,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, Assessment, People, Timeline } from '@mui/icons-material';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import SectionBlock from '../components/SectionBlock';
import jobService from '../services/jobService';
import logger from '../utils/logger';

const COLORS = ['#000000', '#FFD700', '#1a1a1a', '#FFA500', '#2c2c2c', '#FFDF00', '#DAA520'];

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [cityFilter, setCityFilter] = useState('all');
  
  // Analytics data state
  const [jobStats, setJobStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    scheduledToday: 0,
    completionRate: 0
  });
  const [jobVolumeData, setJobVolumeData] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [jobTypeDistribution, setJobTypeDistribution] = useState([]);
  const [cityDistribution, setCityDistribution] = useState([]);
  const [durationAnalysis, setDurationAnalysis] = useState([]);
  const [customerSegmentation, setCustomerSegmentation] = useState([]);
  const [tagInsights, setTagInsights] = useState([]);
  const [jobAchievementMatrix, setJobAchievementMatrix] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [hcpEmployees, setHcpEmployees] = useState([]);
  const [selectedEmployeeJobs, setSelectedEmployeeJobs] = useState([]);
  const [propertySizeDistribution, setPropertySizeDistribution] = useState([]);
  const [serviceFrequencyDistribution, setServiceFrequencyDistribution] = useState([]);
  const [discountAnalysis, setDiscountAnalysis] = useState([]);
  const [avgValueByFrequency, setAvgValueByFrequency] = useState([]);
  const [avgValueByPropertySize, setAvgValueByPropertySize] = useState([]);
  const [revenueTimeframe, setRevenueTimeframe] = useState('daily'); // 'daily', 'weekly', 'monthly'
  const [revenueData, setRevenueData] = useState([]);

  // Fetch analytics data
  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, cityFilter]);

  // Recalculate revenue data when timeframe changes
  useEffect(() => {
    // Only recalculate if we already have jobs data
    if (jobVolumeData.length > 0) {
      // Fetch the jobs again and recalculate
      fetchAnalyticsData();
    }
  }, [revenueTimeframe]);

  // Calculate basic statistics from jobs data when stats endpoint is not available
  // Note: Database status is unreliable - using scheduled date to determine status
  const calculateStatsFromJobs = (jobs) => {
    logger.log('Calculating stats from jobs data for', jobs.length, 'jobs');

    const now = new Date();
    const totalJobs = jobs.length;

    // Determine status based on scheduled date (using utcScheduledDate field)
    const completedJobs = jobs.filter(job => {
      if (!job.utcScheduledDate) return false;
      const jobDate = new Date(job.utcScheduledDate);
      return jobDate < now; // Past jobs are completed
    }).length;

    const scheduledJobs = jobs.filter(job => {
      if (!job.utcScheduledDate) return false;
      const jobDate = new Date(job.utcScheduledDate);
      return jobDate >= now; // Future jobs are scheduled
    }).length;

    // Active jobs are scheduled jobs for today
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    const todayEnd = new Date(now.setHours(23, 59, 59, 999));

    const activeJobs = jobs.filter(job => {
      if (!job.utcScheduledDate) return false;
      const jobDate = new Date(job.utcScheduledDate);
      return jobDate >= todayStart && jobDate <= todayEnd;
    }).length;

    const scheduledToday = activeJobs; // Same as active jobs

    const completionRate = totalJobs > 0 ? ((completedJobs / totalJobs) * 100).toFixed(1) : 0;

    const calculatedStats = {
      totalJobs,
      activeJobs,
      completedJobs,
      scheduledJobs,
      scheduledToday,
      completionRate: parseFloat(completionRate)
    };

    logger.log('Calculated stats (using utcScheduledDate):', calculatedStats);
    return calculatedStats;
  };

  // Parse job categories from specialInstructions field
  const parseJobCategories = (job) => {
    const instructions = job.specialInstructions || job.description || '';
    const parsed = {
      bedrooms: null,
      bathrooms: null,
      frequency: 'One-Time',
      discount: 0,
      hasDiscount: false
    };

    // Extract bedroom count: "2 Bedrooms", "3 Bedroom", "1BR", "2-Bedroom"
    const bedroomMatch = instructions.match(/(\d+)\s*(?:Bedroom|BR|Bed|bedroom)/i);
    if (bedroomMatch) {
      parsed.bedrooms = parseInt(bedroomMatch[1]);
    }

    // Extract bathroom count: "2 Bathrooms", "1.5 Bath", "2BA"
    const bathroomMatch = instructions.match(/(\d+\.?\d*)\s*(?:Bathroom|BA|Bath|bathroom)/i);
    if (bathroomMatch) {
      parsed.bathrooms = parseFloat(bathroomMatch[1]);
    }

    // Extract service frequency
    if (/\b(?:weekly|week)\b/i.test(instructions) && !/\bbi[\s-]?weekly\b/i.test(instructions)) {
      parsed.frequency = 'Weekly';
    } else if (/\bbi[\s-]?weekly\b/i.test(instructions)) {
      parsed.frequency = 'Bi-Weekly';
    } else if (/\b(?:monthly|month)\b/i.test(instructions)) {
      parsed.frequency = 'Monthly';
    } else if (/\b(?:recurring|regular)\b/i.test(instructions)) {
      parsed.frequency = 'Recurring';
    }

    // Extract discount: "20% Off", "10% discount", "15% promo"
    const discountMatch = instructions.match(/(\d+)%\s*(?:off|discount|promo|sale)/i);
    if (discountMatch) {
      parsed.discount = parseInt(discountMatch[1]);
      parsed.hasDiscount = true;
    }

    return parsed;
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Prepare fetch options using the same pattern as MaidMapDisplay
      const fetchOptions = {
        limit: 1000, // Get large dataset for analytics
        tenantId: process.env.REACT_APP_TENANT_ID || 1,
        ...(cityFilter !== 'all' && { cityCode: cityFilter })
      };

      logger.log('🔄 Fetching analytics data with options:', fetchOptions);

      // Fetch jobs and employees separately to isolate failures
      let jobsData = [];
      let employeesData = [];
      
      try {
        logger.log('📋 Fetching jobs...');
        jobsData = await jobService.fetchJobs(fetchOptions);
        logger.log('✅ Jobs data fetched successfully:', {
          type: typeof jobsData,
          keys: Object.keys(jobsData || {}),
          jobsCount: jobsData?.data?.jobs?.length || jobsData?.jobs?.length || 0,
          rawResponse: jobsData // Full response for debugging
        });
      } catch (jobsError) {
        logger.log('❌ Failed to fetch jobs:', jobsError);
        logger.log('❌ Full error object:', {
          message: jobsError.message,
          response: jobsError.response?.data,
          status: jobsError.response?.status
        });
        throw new Error(`Jobs API failed: ${jobsError.message}`);
      }

      try {
        logger.log('👥 Fetching employees...');
        employeesData = await jobService.fetchEmployees(fetchOptions);
        logger.log('✅ Employees data fetched successfully:', {
          type: typeof employeesData,
          keys: Object.keys(employeesData || {}),
          employeesCount: employeesData?.employees?.length || 0
        });
      } catch (employeesError) {
        logger.log('❌ Failed to fetch employees:', employeesError);
        // Don't throw for employees - continue with jobs only
        employeesData = { employees: [] };
        logger.log('⚠️ Continuing analytics without employee data');
      }

      // Extract jobs from the response structure (same as MaidMapDisplay pattern)
      const jobs = jobsData.data?.jobs || jobsData.jobs || [];
      logger.log('📊 Extracted jobs array:', {
        jobsLength: jobs.length,
        firstJob: jobs[0],
        dataPath: jobsData.data ? 'jobsData.data.jobs' : 'jobsData.jobs'
      });

      // Extract employees from the response structure
      const employees = employeesData.employees || [];
      setHcpEmployees(employees);
      logger.log(`👥 Fetched ${employees.length} employees from HCP API`, {
        firstEmployee: employees[0]
      });
      
      // Calculate stats from jobs data (no separate stats endpoint for external jobs)
      logger.log('Calculating stats from jobs data since using external job source');
      const statsData = calculateStatsFromJobs(jobs);
      
      // Process the data for analytics
      processAnalyticsData(jobs, statsData);
      
    } catch (error) {
      logger.log('❌ Analytics data fetch failed:', error);
      
      // Provide helpful error messages based on error type
      let userFriendlyError = 'Failed to fetch analytics data';
      if (error.message?.includes('Jobs API failed')) {
        userFriendlyError = 'Unable to load job data. Please check if the backend service is running.';
      } else if (error.message?.includes('Network Error')) {
        userFriendlyError = 'Network connection issue. Please check your internet connection.';
      } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        userFriendlyError = 'Authentication required. Please log in again.';
      } else {
        userFriendlyError = `Analytics service error: ${error.message}`;
      }
      
      setError(userFriendlyError);
    } finally {
      setLoading(false);
    }
  };

  const calculateRevenueData = (jobs) => {
    logger.log('Calculating revenue data for', jobs.length, 'jobs', 'with timeframe:', revenueTimeframe);

    // Group revenue by date
    const revenueByDate = {};
    jobs.forEach(job => {
      const dateValue = job.utcScheduledDate || job.utcImportedAt;
      if (dateValue && job.totalAmount) {
        const jobDate = new Date(dateValue);
        let key;

        if (revenueTimeframe === 'daily') {
          // Group by day (YYYY-MM-DD)
          key = jobDate.toISOString().split('T')[0];
        } else if (revenueTimeframe === 'weekly') {
          // Group by week (ISO week start date)
          const weekStart = new Date(jobDate);
          const day = weekStart.getDay();
          const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
          weekStart.setDate(diff);
          key = weekStart.toISOString().split('T')[0];
        } else if (revenueTimeframe === 'monthly') {
          // Group by month (YYYY-MM)
          key = `${jobDate.getFullYear()}-${String(jobDate.getMonth() + 1).padStart(2, '0')}`;
        }

        if (key) {
          if (!revenueByDate[key]) {
            revenueByDate[key] = { revenue: 0, jobCount: 0 };
          }
          revenueByDate[key].revenue += job.totalAmount;
          revenueByDate[key].jobCount += 1;
        }
      }
    });

    // Convert to array and sort by date
    const revenueDataArray = Object.entries(revenueByDate)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([dateKey, data]) => {
        let displayDate;
        if (revenueTimeframe === 'daily') {
          displayDate = new Date(dateKey).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (revenueTimeframe === 'weekly') {
          const weekStart = new Date(dateKey);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          displayDate = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        } else if (revenueTimeframe === 'monthly') {
          const [year, month] = dateKey.split('-');
          displayDate = new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        }

        return {
          date: displayDate,
          revenue: Math.round(data.revenue / 100), // Convert cents to dollars
          jobCount: data.jobCount,
          avgJobValue: Math.round(data.revenue / data.jobCount / 100) // Average per job in dollars
        };
      });

    setRevenueData(revenueDataArray);
    logger.log(`Revenue data calculated: ${revenueDataArray.length} time periods with timeframe ${revenueTimeframe}`);
  };

  const processAnalyticsData = (jobs, stats) => {
    logger.log('Processing analytics data for', jobs.length, 'jobs');

    // Set basic stats
    setJobStats(stats);

    // Declare now at the top of function scope for use throughout
    const now = new Date();

    // Filter jobs by date range if specified
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateRange));

    const filteredJobs = dateRange === 'all'
      ? jobs
      : jobs.filter(job => {
          // Use utcScheduledDate field (HCP API format)
          const dateValue = job.utcScheduledDate || job.utcImportedAt;
          if (!dateValue) {
            logger.log(`⚠️ Job ${job.id} has no utcScheduledDate field`);
            return false;
          }
          const jobDate = new Date(dateValue);
          return jobDate >= cutoffDate;
        });

    logger.log(`Filtered to ${filteredJobs.length} jobs for date range: ${dateRange} (from ${jobs.length} total)`);

    // Process job volume over time
    const volumeByDate = {};
    filteredJobs.forEach(job => {
      const dateValue = job.utcScheduledDate || job.utcImportedAt;
      if (dateValue) {
        const date = new Date(dateValue).toISOString().split('T')[0];
        volumeByDate[date] = (volumeByDate[date] || 0) + 1;
      }
    });
    
    const volumeData = Object.entries(volumeByDate)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, count]) => ({
        date: new Date(date).toLocaleDateString(),
        jobs: count
      }));
    setJobVolumeData(volumeData);

    // Process revenue data (will be aggregated based on selected timeframe)
    calculateRevenueData(filteredJobs);

    // Process status distribution using scheduled date (ignoring database status)
    const statusCounts = {
      'COMPLETED': 0,
      'SCHEDULED': 0,
      'NO_DATE': 0
    };

    filteredJobs.forEach(job => {
      if (!job.utcScheduledDate) {
        statusCounts['NO_DATE']++;
      } else {
        const jobDate = new Date(job.utcScheduledDate);
        if (jobDate < now) {
          statusCounts['COMPLETED']++;
        } else {
          statusCounts['SCHEDULED']++;
        }
      }
    });

    const statusData = Object.entries(statusCounts)
      .filter(([, count]) => count > 0) // Only show statuses with jobs
      .map(([status, count]) => ({
        name: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        value: count,
        percentage: filteredJobs.length > 0 ? ((count / filteredJobs.length) * 100).toFixed(1) : 0,
        color: jobService.getJobStatusColor(status)
      }));
    setStatusDistribution(statusData);

    // Process job type distribution
    const typeCounts = {};
    filteredJobs.forEach(job => {
      // Use consistent job type extraction pattern
      const type = job.jobType || job.booking?.propertyType || 'RESIDENTIAL';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    
    const typeData = Object.entries(typeCounts).map(([type, count]) => ({
      name: type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
      value: count,
      color: jobService.getJobStatusColor(type)
    }));
    setJobTypeDistribution(typeData);

    // Process city distribution
    const cityCounts = {};
    filteredJobs.forEach(job => {
      const city = job.cityCode || job.booking?.cityCode || 'Unknown';
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });
    
    const cityData = Object.entries(cityCounts).map(([city, count]) => ({
      name: city,
      value: count
    }));
    setCityDistribution(cityData);

    // Process duration analysis (estimated vs actual)
    const durationData = filteredJobs
      .filter(job => job.estimatedDuration && job.actualDuration)
      .slice(0, 20) // Limit for chart readability
      .map((job, index) => ({
        jobId: job.jobNumber || job.id?.toString().slice(-6) || `Job ${index + 1}`,
        estimated: job.estimatedDuration,
        actual: job.actualDuration,
        efficiency: job.actualDuration > 0 ? ((job.estimatedDuration / job.actualDuration) * 100).toFixed(1) : 0
      }));
    setDurationAnalysis(durationData);

    // Process customer segmentation based on actual job data
    const jobValues = filteredJobs
      .map(job => job.totalAmount || 0)
      .filter(amount => amount > 0)
      .sort((a, b) => b - a); // Sort descending

    if (jobValues.length > 0) {
      const highValueThreshold = jobValues[Math.floor(jobValues.length * 0.2)] || 30000; // Top 20%
      const regularThreshold = jobValues[Math.floor(jobValues.length * 0.8)] || 10000; // Bottom 20%

      const customerData = [
        { 
          segment: 'High Value', 
          count: jobValues.filter(v => v >= highValueThreshold).length,
          avgSpend: Math.round((jobValues.filter(v => v >= highValueThreshold).reduce((sum, v) => sum + v, 0) / jobValues.filter(v => v >= highValueThreshold).length || 0) / 100)
        },
        { 
          segment: 'Regular', 
          count: jobValues.filter(v => v < highValueThreshold && v >= regularThreshold).length,
          avgSpend: Math.round((jobValues.filter(v => v < highValueThreshold && v >= regularThreshold).reduce((sum, v) => sum + v, 0) / jobValues.filter(v => v < highValueThreshold && v >= regularThreshold).length || 0) / 100)
        },
        { 
          segment: 'One-time', 
          count: jobValues.filter(v => v < regularThreshold).length,
          avgSpend: Math.round((jobValues.filter(v => v < regularThreshold).reduce((sum, v) => sum + v, 0) / jobValues.filter(v => v < regularThreshold).length || 0) / 100)
        }
      ];
      setCustomerSegmentation(customerData);
    } else {
      // Fallback data if no job values available
      setCustomerSegmentation([
        { segment: 'High Value', count: 0, avgSpend: 0 },
        { segment: 'Regular', count: 0, avgSpend: 0 },
        { segment: 'One-time', count: 0, avgSpend: 0 }
      ]);
    }

    // Process tag insights from actual job data
    const tagCounts = {};

    filteredJobs.forEach(job => {
      // Extract tags from various job fields
      const tags = [];

      // Add job type as a tag
      if (job.jobType) tags.push(job.jobType.replace(/_/g, ' '));

      // Add property type as a tag
      if (job.booking?.propertyType) tags.push(job.booking.propertyType.replace(/_/g, ' '));

      // Add status-based tags using scheduled date
      if (job.utcScheduledDate) {
        const jobDate = new Date(job.utcScheduledDate);
        if (jobDate < now) {
          tags.push('Completed Service');
        } else {
          tags.push('Scheduled Service');
        }
      }

      // Add value-based tags
      const jobValue = job.totalAmount || 0;
      if (jobValue > 50000) tags.push('Premium Service'); // $500+
      if (jobValue > 30000) tags.push('Large Job'); // $300+

      tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });

    const tagInsights = Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a) // Sort by frequency
      .slice(0, 8) // Top 8 tags
      .map(([tag, frequency]) => ({
        tag,
        frequency
      }));
    
    setTagInsights(tagInsights);

    // Parse job categories from specialInstructions
    logger.log('Parsing job categories from specialInstructions field');
    const parsedJobs = filteredJobs.map(job => ({
      ...job,
      categories: parseJobCategories(job)
    }));

    // Process property size distribution (bedrooms/bathrooms)
    const propertySizes = {};
    parsedJobs.forEach(job => {
      const { bedrooms, bathrooms } = job.categories;
      if (bedrooms !== null || bathrooms !== null) {
        const sizeKey = `${bedrooms || '?'}BR / ${bathrooms || '?'}BA`;
        propertySizes[sizeKey] = (propertySizes[sizeKey] || 0) + 1;
      }
    });

    const propertySizeData = Object.entries(propertySizes)
      .sort(([,a], [,b]) => b - a) // Sort by frequency
      .map(([size, count]) => ({
        name: size,
        value: count
      }));
    setPropertySizeDistribution(propertySizeData);
    logger.log('Property size distribution:', propertySizeData);

    // Process service frequency distribution
    const frequencyCounts = {};
    parsedJobs.forEach(job => {
      const frequency = job.categories.frequency;
      frequencyCounts[frequency] = (frequencyCounts[frequency] || 0) + 1;
    });

    const frequencyData = Object.entries(frequencyCounts)
      .map(([frequency, count]) => ({
        name: frequency,
        value: count,
        percentage: filteredJobs.length > 0 ? ((count / filteredJobs.length) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.value - a.value); // Sort by frequency
    setServiceFrequencyDistribution(frequencyData);
    logger.log('Service frequency distribution:', frequencyData);

    // Process discount analysis
    const discountCounts = { 'No Discount': 0, '10% Off': 0, '15% Off': 0, '20% Off': 0, '25% Off': 0, 'Other Discount': 0 };
    let totalDiscountedAmount = 0;
    let totalDiscountValue = 0;

    parsedJobs.forEach(job => {
      if (job.categories.hasDiscount) {
        const discount = job.categories.discount;
        if (discount === 10) discountCounts['10% Off']++;
        else if (discount === 15) discountCounts['15% Off']++;
        else if (discount === 20) discountCounts['20% Off']++;
        else if (discount === 25) discountCounts['25% Off']++;
        else discountCounts['Other Discount']++;

        // Calculate discount value
        const jobValue = job.totalAmount || 0;
        totalDiscountedAmount += jobValue;
        totalDiscountValue += (jobValue * discount) / 100;
      } else {
        discountCounts['No Discount']++;
      }
    });

    const discountData = Object.entries(discountCounts)
      .filter(([, count]) => count > 0) // Only show categories with data
      .map(([discount, count]) => ({
        name: discount,
        value: count,
        percentage: filteredJobs.length > 0 ? ((count / filteredJobs.length) * 100).toFixed(1) : 0
      }));
    setDiscountAnalysis(discountData);
    logger.log('Discount analysis:', discountData);
    logger.log(`Total discount value: $${(totalDiscountValue / 100).toFixed(2)}`);

    // Calculate average job value by service frequency
    const frequencyValues = {};
    parsedJobs.forEach(job => {
      const frequency = job.categories.frequency;
      const jobValue = job.totalAmount || 0;
      if (!frequencyValues[frequency]) {
        frequencyValues[frequency] = { total: 0, count: 0, maxAmount: 0, minAmount: Infinity };
      }
      frequencyValues[frequency].total += jobValue;
      frequencyValues[frequency].count += 1;
      frequencyValues[frequency].maxAmount = Math.max(frequencyValues[frequency].maxAmount, jobValue);
      frequencyValues[frequency].minAmount = Math.min(frequencyValues[frequency].minAmount, jobValue);
    });

    const avgFrequencyData = Object.entries(frequencyValues)
      .map(([frequency, data]) => ({
        name: frequency,
        avgValue: data.count > 0 ? Math.round(data.total / data.count / 100) : 0, // Convert to dollars
        jobCount: data.count,
        maxValue: data.count > 0 ? Math.round(data.maxAmount / 100) : 0,
        minValue: data.count > 0 ? Math.round(data.minAmount / 100) : 0
      }))
      .sort((a, b) => b.avgValue - a.avgValue); // Sort by average value descending
    setAvgValueByFrequency(avgFrequencyData);
    logger.log('Average value by service frequency:', avgFrequencyData);

    // Calculate average job value by property size
    const propertySizeValues = {};
    parsedJobs.forEach(job => {
      const { bedrooms, bathrooms } = job.categories;
      if (bedrooms !== null || bathrooms !== null) {
        const sizeKey = `${bedrooms || '?'}BR / ${bathrooms || '?'}BA`;
        const jobValue = job.totalAmount || 0;
        if (!propertySizeValues[sizeKey]) {
          propertySizeValues[sizeKey] = { total: 0, count: 0, maxAmount: 0, minAmount: Infinity };
        }
        propertySizeValues[sizeKey].total += jobValue;
        propertySizeValues[sizeKey].count += 1;
        propertySizeValues[sizeKey].maxAmount = Math.max(propertySizeValues[sizeKey].maxAmount, jobValue);
        propertySizeValues[sizeKey].minAmount = Math.min(propertySizeValues[sizeKey].minAmount, jobValue);
      }
    });

    const avgPropertySizeData = Object.entries(propertySizeValues)
      .map(([size, data]) => ({
        name: size,
        avgValue: data.count > 0 ? Math.round(data.total / data.count / 100) : 0, // Convert to dollars
        jobCount: data.count,
        maxValue: data.count > 0 ? Math.round(data.maxAmount / 100) : 0,
        minValue: data.count > 0 ? Math.round(data.minAmount / 100) : 0
      }))
      .sort((a, b) => b.avgValue - a.avgValue); // Sort by average value descending
    setAvgValueByPropertySize(avgPropertySizeData);
    logger.log('Average value by property size:', avgPropertySizeData);

    // Process job achievement matrix
    processJobAchievementMatrix(filteredJobs);

    logger.log('Analytics data processing completed');
  };

  const processJobAchievementMatrix = (jobs) => {
    logger.log('Processing job achievement matrix for', jobs.length, 'jobs');

    // Use HCP employees as the master list
    const hcpEmployeeNames = hcpEmployees.map(emp => emp.name).sort();
    logger.log(`Using ${hcpEmployeeNames.length} HCP employees for matrix:`, hcpEmployeeNames);

    const jobEmployeeData = [];
    const now = new Date();

    jobs.forEach(job => {
      // Determine status based on scheduled date (using utcScheduledDate)
      let computedStatus = 'NO_DATE';
      if (job.utcScheduledDate) {
        const jobDate = new Date(job.utcScheduledDate);
        computedStatus = jobDate < now ? 'COMPLETED' : 'SCHEDULED';
      }

      const jobData = {
        id: job.id,
        jobNumber: job.jobNumber || job.hcpJobNumber,
        customerName: job.booking?.customerName || 'Unknown Customer',
        totalAmount: job.totalAmount || 0,
        status: computedStatus, // Use computed status instead of database status
        employees: {}
      };
      
      // Extract employees from maid assignments and match with HCP employees
      const jobEmployeeNames = new Set();
      
      if (job.maidAssignments?.allMaids && Array.isArray(job.maidAssignments.allMaids)) {
        job.maidAssignments.allMaids.forEach(maid => {
          if (maid.name) {
            jobEmployeeNames.add(maid.name);
          }
        });
      }
      
      // If lead maid exists
      if (job.maidAssignments?.leadMaid?.maidName) {
        jobEmployeeNames.add(job.maidAssignments.leadMaid.maidName);
      }
      
      // Map job employees to HCP employees and calculate hours
      jobEmployeeNames.forEach(employeeName => {
        // Check if this employee exists in HCP employee list
        const hcpEmployee = hcpEmployees.find(emp => 
          emp.name === employeeName || 
          emp.name.toLowerCase() === employeeName.toLowerCase()
        );
        
        if (hcpEmployee) {
          // Calculate hours worked (use estimatedDuration as default if no actual hours)
          const hoursWorked = job.actualDuration ? (job.actualDuration / 60) : (job.estimatedDuration ? (job.estimatedDuration / 60) : 2);
          jobData.employees[hcpEmployee.name] = hoursWorked;
          logger.log(`Matched job employee "${employeeName}" to HCP employee "${hcpEmployee.name}" with ${hoursWorked}h`);
        } else {
          logger.log(`Job employee "${employeeName}" not found in HCP employee list`);
        }
      });
      
      jobEmployeeData.push(jobData);
    });
    
    setEmployeeList(hcpEmployeeNames);
    setJobAchievementMatrix(jobEmployeeData);
    
    logger.log('Job achievement matrix processing completed with HCP employees');
  };

  const fetchJobsForSpecificEmployee = async (employeeId, employeeName) => {
    try {
      logger.log(`Fetching jobs for employee ${employeeName} (${employeeId})`);
      
      const employeeJobsData = await jobService.fetchJobsForEmployee(employeeId, {
        work_status: ['completed', 'in_progress'],
        limit: 50
      });
      
      const jobs = employeeJobsData.data?.jobs || employeeJobsData.jobs || [];
      setSelectedEmployeeJobs(jobs);
      
      logger.log(`Fetched ${jobs.length} jobs for employee ${employeeName}:`, jobs);
      
      // Show alert with results
      alert(`Found ${jobs.length} jobs for ${employeeName}. Check console for details.`);
      
    } catch (error) {
      logger.log('Error fetching jobs for employee:', error);
      alert(`Error fetching jobs for ${employeeName}: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout fullWidth>
        <SectionBlock title="Analytics Dashboard" bgcolor="background.default" py={{ xs: 4, md: 8 }} maxWidth="xl">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress size={60} />
          </Box>
        </SectionBlock>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout fullWidth>
        <SectionBlock title="Analytics Dashboard" bgcolor="background.default" py={{ xs: 4, md: 8 }} maxWidth="xl">
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
          
          {/* Debug Tools */}
          <Paper sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>🔧 Debug Tools</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use these tools to debug the Analytics Dashboard connection issues:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={async () => {
                  try {
                    logger.log('🧪 Testing direct API call to /api/jobs...');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/jobs?limit=5`, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    const data = await response.json();
                    logger.log('✅ Direct API test result:', { status: response.status, data });
                    alert(`API Test: ${response.ok ? 'SUCCESS' : 'FAILED'} (${response.status})\nCheck console for details`);
                  } catch (err) {
                    logger.log('❌ Direct API test failed:', err);
                    alert(`API Test FAILED: ${err.message}`);
                  }
                }}
              >
                Test Jobs API
              </Button>
              
              <Button 
                variant="outlined" 
                size="small"
                onClick={async () => {
                  try {
                    logger.log('🧪 Testing direct API call to /api/employees...');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/employees?limit=5`, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    const data = await response.json();
                    logger.log('✅ Direct API test result:', { status: response.status, data });
                    alert(`API Test: ${response.ok ? 'SUCCESS' : 'FAILED'} (${response.status})\nCheck console for details`);
                  } catch (err) {
                    logger.log('❌ Direct API test failed:', err);
                    alert(`API Test FAILED: ${err.message}`);
                  }
                }}
              >
                Test Employees API
              </Button>
              
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  logger.log('🔍 Environment Check:', {
                    API_URL: process.env.REACT_APP_API_URL,
                    TENANT_ID: process.env.REACT_APP_TENANT_ID,
                    CITY_CODE: process.env.REACT_APP_CITY_CODE,
                    hasToken: !!localStorage.getItem('token')
                  });
                  alert('Environment logged to console');
                }}
              >
                Check Environment
              </Button>
              
              <Button 
                variant="contained" 
                size="small"
                color="primary"
                onClick={() => {
                  setError(null);
                  fetchAnalyticsData();
                }}
              >
                Retry Analytics
              </Button>
            </Box>
          </Paper>
        </SectionBlock>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout fullWidth>
      <SectionBlock title="Analytics Dashboard" bgcolor="background.default" py={{ xs: 4, md: 8 }} maxWidth="xl">
        
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="365">Last year</MenuItem>
                  <MenuItem value="all">All time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={cityFilter}
                  label="City"
                  onChange={(e) => setCityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Cities</MenuItem>
                  <MenuItem value="ORL">Orlando</MenuItem>
                  <MenuItem value="MCO">Metro Orlando</MenuItem>
                  <MenuItem value="TAM">Tampa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Key Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #000000 0%, #FFD700 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {jobStats.totalJobs}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Jobs
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #FFA500 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {jobStats.activeJobs}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Jobs
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #2c2c2c 0%, #FFDF00 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {jobStats.completedJobs}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Completed Jobs
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #DAA520 0%, #000000 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {jobStats.completionRate}%
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Completion Rate
                    </Typography>
                  </Box>
                  <Timeline sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Revenue Graph */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Revenue Over Time
              </Typography>
              <ToggleButtonGroup
                value={revenueTimeframe}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setRevenueTimeframe(newValue);
                  }
                }}
                size="small"
              >
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={revenueTimeframe === 'daily' ? -45 : -15}
                  textAnchor="end"
                  height={revenueTimeframe === 'daily' ? 100 : 80}
                />
                <YAxis
                  label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Paper sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.98)', boxShadow: 3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {data.date}
                          </Typography>
                          <Typography variant="h6" color="success.main" sx={{ mb: 0.5 }}>
                            ${data.revenue.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {data.jobCount} job{data.jobCount !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            Avg: ${data.avgJobValue.toLocaleString()} per job
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#FFD700" name="Revenue ($)">
                  {revenueData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Charts Grid */}
        <Grid container spacing={3}>
          
          {/* Job Volume Chart */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Job Volume Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={jobVolumeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="jobs" stroke="#FFD700" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Distribution */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Job Status Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} (${percentage}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Property Size Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Property Size Distribution
                </Typography>
                {propertySizeDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={propertySizeDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#FFD700">
                        {propertySizeDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography variant="body1" color="text.secondary">
                      No property size data available. Jobs with bedroom/bathroom info will show here.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* City Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Jobs by City
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={cityDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="value" fill="#FFD700" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Frequency Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Service Frequency Distribution
                </Typography>
                {serviceFrequencyDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={serviceFrequencyDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name} (${percentage}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {serviceFrequencyDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value, name, props) => [
                        `${value} jobs`,
                        `${props.payload.name} (${props.payload.percentage}%)`
                      ]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography variant="body1" color="text.secondary">
                      No service frequency data available. Jobs with frequency info will show here.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Discount Analysis */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Discount & Promotion Analysis
                </Typography>
                {discountAnalysis.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={discountAnalysis}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name} (${percentage}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {discountAnalysis.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value, name, props) => [
                        `${value} jobs`,
                        `${props.payload.name} (${props.payload.percentage}%)`
                      ]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography variant="body1" color="text.secondary">
                      No discount data available. Jobs with discounts will show here.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Segmentation */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Customer Segmentation
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerSegmentation}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ segment, count, percent }) => `${segment}: ${count} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {customerSegmentation.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value, name, props) => [
                      `${value} customers`,
                      `${props.payload.segment} (Avg: $${props.payload.avgSpend})`
                    ]} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Average Job Value by Service Frequency */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Average Job Value by Service Frequency
                </Typography>
                {avgValueByFrequency.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={avgValueByFrequency}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-15} textAnchor="end" height={70} />
                      <YAxis label={{ value: 'Average $ Value', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Paper sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.95)' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {data.name}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                  Avg: ${data.avgValue}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  Jobs: {data.jobCount}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'success.main' }}>
                                  Highest: ${data.maxValue}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'error.main' }}>
                                  Lowest: ${data.minValue}
                                </Typography>
                              </Paper>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="avgValue" fill="#FFD700">
                        {avgValueByFrequency.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography variant="body1" color="text.secondary">
                      No service frequency data available. Jobs with frequency info will show here.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Average Job Value by Property Size */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Average Job Value by Property Size
                </Typography>
                {avgValueByPropertySize.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={avgValueByPropertySize}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis label={{ value: 'Average $ Value', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Paper sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.95)' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {data.name}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                  Avg: ${data.avgValue}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  Jobs: {data.jobCount}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'success.main' }}>
                                  Highest: ${data.maxValue}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'error.main' }}>
                                  Lowest: ${data.minValue}
                                </Typography>
                              </Paper>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="avgValue" fill="#000000">
                        {avgValueByPropertySize.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography variant="body1" color="text.secondary">
                      No property size data available. Jobs with bedroom/bathroom info will show here.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Employee List */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  HCP Employees ({hcpEmployees.length})
                </Typography>
                {hcpEmployees.length > 0 ? (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <Grid container spacing={1}>
                      {hcpEmployees.map((employee) => (
                        <Grid item xs={12} sm={6} md={12} lg={6} key={employee.id}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              border: '1px solid', 
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {employee.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {employee.role} • ID: {employee.id}
                                </Typography>
                              </Box>
                              <Chip 
                                size="small" 
                                label={employee.status}
                                color={employee.status === 'ACTIVE' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => fetchJobsForSpecificEmployee(employee.id, employee.name)}
                              sx={{ fontSize: '0.7rem', py: 0.5 }}
                            >
                              Test Fetch Jobs
                            </Button>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                    <Typography variant="body1" color="text.secondary">
                      No employees found in HCP system.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Job Achievement Matrix */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Job Achievement Matrix - Employee Hours per Job
                </Typography>
                {jobAchievementMatrix.length > 0 && employeeList.length > 0 ? (
                  <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Job</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Customer</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Status</TableCell>
                          {employeeList.map(employee => (
                            <TableCell 
                              key={employee} 
                              align="center" 
                              sx={{ fontWeight: 'bold', bgcolor: 'grey.100', minWidth: 80 }}
                            >
                              <Tooltip title={employee}>
                                <span>{employee.length > 10 ? employee.substring(0, 10) + '...' : employee}</span>
                              </Tooltip>
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                            Job Total ($)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobAchievementMatrix.slice(0, 20).map((job) => (
                          <TableRow key={job.id} hover>
                            <TableCell>
                              <Tooltip title={`Job ID: ${job.id}`}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {job.jobNumber}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {job.customerName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={job.status.replace(/_/g, ' ')}
                                color={
                                  job.status === 'COMPLETED' ? 'success' :
                                  job.status === 'SCHEDULED' ? 'primary' :
                                  'default'
                                }
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            {employeeList.map(employee => (
                              <TableCell key={employee} align="center">
                                {job.employees[employee] ? (
                                  <Chip 
                                    size="small" 
                                    label={`${job.employees[employee].toFixed(1)}h`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">-</Typography>
                                )}
                              </TableCell>
                            ))}
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                ${(job.totalAmount / 100).toFixed(0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <Typography variant="body1" color="text.secondary">
                      No job assignment data available. Jobs with employee assignments will show in the matrix.
                    </Typography>
                  </Box>
                )}
                {jobAchievementMatrix.length > 20 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing first 20 jobs. Use date filters to narrow results.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </SectionBlock>
    </AdminPageLayout>
  );
};

export default Analytics;